import axios from 'axios';
import { DailyReport, Project, ApiResponse, TimeEntry, Sekurant, SekurantenProjekt } from '../types/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://rooftopapp.hirnworx.com/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request Interceptor for JWT Token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response Interceptor for Error Handling with Token Refresh
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: any) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: any = null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        // No refresh token available, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Try to refresh the access token
        const response = await api.post<{access_token: string; refresh_token?: string}>('/auth/refresh', {
          refresh_token: refreshToken
        });
        
        const { access_token, refresh_token: new_refresh_token } = response.data;
        
        // Store new tokens
        localStorage.setItem('token', access_token);
        if (new_refresh_token) {
          localStorage.setItem('refresh_token', new_refresh_token);
        }
        
        // Update the authorization header
        api.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
        originalRequest.headers['Authorization'] = 'Bearer ' + access_token;
        
        // Process queued requests with new token
        processQueue(null, access_token);
        
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username: string, password: string) => {
    const response = await api.post('/auth/login', new URLSearchParams({
      username,
      password,
    }), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  },
  getUser: async () => {
    const response = await api.get('/auth/users/me');
    return response.data;
  },
};

// Projects API
export const projectsAPI = {
  getProjects: async (): Promise<Project[]> => {
    const response = await api.get<Project[]>('/projects');
    return response.data;
  },

  getProject: async (id: number): Promise<Project> => {
    const response = await api.get<Project>(`/projects/${id}`);
    return response.data;
  },

  createProject: async (data: Omit<Project, 'id' | 'created_at' | 'updated_at'>): Promise<Project> => {
    const response = await api.post<Project>('/projects', data);
    return response.data;
  },

  updateProject: async (id: number, data: Partial<Project>): Promise<Project> => {
    const response = await api.put<Project>(`/projects/${id}`, data);
    return response.data;
  },

  deleteProject: async (id: number): Promise<void> => {
    await api.delete(`/projects/${id}`);
  },
};

// Time Entries API
export const timeEntriesAPI = {
  getEntries: async (): Promise<TimeEntry[]> => {
    const response = await api.get<TimeEntry[]>('/time-entries');
    return response.data;
  },

  getEntry: async (id: number): Promise<TimeEntry> => {
    const response = await api.get<TimeEntry>(`/time-entries/${id}`);
    return response.data;
  },

  createEntry: async (data: Omit<TimeEntry, 'id' | 'created_at' | 'updated_at'>): Promise<TimeEntry> => {
    const response = await api.post<TimeEntry>('/time-entries', data);
    return response.data;
  },

  updateEntry: async (id: number, data: Partial<TimeEntry>): Promise<TimeEntry> => {
    const response = await api.put<TimeEntry>(`/time-entries/${id}`, data);
    return response.data;
  },

  deleteEntry: async (id: number): Promise<void> => {
    await api.delete(`/time-entries/${id}`);
  },
};

// Regie Reports API (früher Daily Reports)
export const regieReportsAPI = {
  getReports: async (): Promise<ApiResponse<DailyReport>> => {
    const response = await api.get<ApiResponse<DailyReport>>('/regie-reports');
    return response.data;
  },

  getReport: async (id: number): Promise<DailyReport> => {
    const response = await api.get<DailyReport>(`/regie-reports/${id}`);
    return response.data;
  },

  createReport: async (data: FormData): Promise<DailyReport> => {
    const response = await api.post<DailyReport>('/regie-reports', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  updateReport: async (id: number, data: FormData): Promise<DailyReport> => {
    const response = await api.put<DailyReport>(`/regie-reports/${id}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteReport: async (id: number): Promise<void> => {
    await api.delete(`/regie-reports/${id}`);
  },

  deletePhoto: async (photoId: number): Promise<void> => {
    await api.delete(`/regie-reports/photo/${photoId}`);
  },

  getNextNumber: async (): Promise<{ next_number: string }> => {
    const response = await api.get<{ next_number: string }>('/regie-reports/next-number');
    return response.data;
  },
};

// Sekuranten Projekte API
export const sekurantenProjekteAPI = {
  getProjekte: async (project_id?: number): Promise<SekurantenProjekt[]> => {
    let url = '/sekuranten-projekte';
    if (project_id) {
      url += `?project_id=${project_id}`;
    }
    const response = await api.get<SekurantenProjekt[]>(url);
    return response.data;
  },

  getProjekt: async (id: number): Promise<SekurantenProjekt> => {
    const response = await api.get<SekurantenProjekt>(`/sekuranten-projekte/${id}`);
    return response.data;
  },

  createProjekt: async (data: Partial<SekurantenProjekt> | FormData): Promise<SekurantenProjekt> => {
    let config = {};
    if (data instanceof FormData) {
      config = { headers: { 'Content-Type': 'multipart/form-data' } };
    }
    const response = await api.post<SekurantenProjekt>('/sekuranten-projekte', data, config);
    return response.data;
  },

  updateProjekt: async (id: number, data: Partial<SekurantenProjekt> | FormData): Promise<SekurantenProjekt> => {
    let config = {};
    if (data instanceof FormData) {
      config = { headers: { 'Content-Type': 'multipart/form-data' } };
    }
    const response = await api.put<SekurantenProjekt>(`/sekuranten-projekte/${id}`, data, config);
    return response.data;
  },

  deleteProjekt: async (id: number): Promise<void> => {
    await api.delete(`/sekuranten-projekte/${id}`);
  },

  uploadPdfPlan: async (id: number, file: File): Promise<{ message: string; file_path: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{ message: string; file_path: string }>(
      `/sekuranten-projekte/${id}/pdf-plan`, 
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  deletePdfPlan: async (id: number): Promise<void> => {
    await api.delete(`/sekuranten-projekte/${id}/pdf-plan`);
  },
};

// Sekuranten API
export const sekurantenAPI = {
  getSekuranten: async (sekuranten_projekt_id?: number): Promise<Sekurant[]> => {
    let url = '/sekuranten';
    if (sekuranten_projekt_id) {
      url += `?sekuranten_projekt_id=${sekuranten_projekt_id}`;
    }
    const response = await api.get<Sekurant[]>(url);
    return response.data;
  },

  getSekurant: async (id: number): Promise<Sekurant> => {
    const response = await api.get<Sekurant>(`/sekuranten/${id}`);
    return response.data;
  },

  createSekurant: async (data: FormData): Promise<{ message: string; doc_id: number }> => {
    const response = await api.post<{ message: string; doc_id: number }>('/sekuranten', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  updateSekurant: async (id: number, data: Partial<Sekurant>): Promise<{ message: string; doc_id: number }> => {
    const response = await api.put<{ message: string; doc_id: number }>(`/sekuranten/${id}`, data);
    return response.data;
  },

  deleteSekurant: async (id: number): Promise<void> => {
    await api.delete(`/sekuranten/${id}`);
  },

  addPhoto: async (id: number, photo: File): Promise<{ message: string; photo_id: number; file_path: string }> => {
    const formData = new FormData();
    formData.append('photo', photo);
    const response = await api.post<{ message: string; photo_id: number; file_path: string }>(`/sekuranten/${id}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deletePhoto: async (photoId: number): Promise<void> => {
    await api.delete(`/sekuranten/photo/${photoId}`);
  },
};

export async function createRegieReport(data: any) {
  // Serialisiere JSON-Felder NICHT mehr, sondern sende als Array
  const payload = {
    ...data,
    beauftragte_personen: data.beauftragte_personen,
    mitarbeiter: data.mitarbeiter,
  };
  return api.post('/regie-reports', payload);
}

export async function updateRegieReport(id: number, data: any) {
  const payload = {
    ...data,
    beauftragte_personen: data.beauftragte_personen,
    mitarbeiter: data.mitarbeiter,
  };
  return api.put(`/regie-reports/${id}`, payload);
}

// Backward compatibility alias
export const dailyReportsAPI = regieReportsAPI;
// Backward compatibility
export const createDailyReport = createRegieReport;
export const updateDailyReport = updateRegieReport;

export default api; 